import { useEffect, useMemo, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import Select from 'react-select';
import AppContext from '../contexts/AppContext';
import { useCmdbChain } from '../hooks/useCmdbChain';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import AttachmentsGallery from './AttachmentsGallery';
import UploadProgressIndicator from './UploadProgressIndicator';
import { isProformaSectionVisible, isProformaQuestionVisible } from '../utils/proformaConditions';
import { isDraftIssue } from '../utils/draftConfig';

const MAX_FILES = 4;
const DESIGN_ANSWER_FALLBACK_FIELDS = new Set(['customfield_10627', 'customfield_10629']);

// Jira validates rich text ('rt') answers on the `adf` key only — a `text` answer
// is ignored and a required rt question fails submit with 422 "Field is required".
const textToAdf = (text) => ({
  version: 1,
  type: 'doc',
  content: text
    ? text.split('\n').map((line) => ({
        type: 'paragraph',
        content: line ? [{ type: 'text', text: line }] : [],
      }))
    : [],
});

const adfToText = (node) => {
  if (!node) return '';
  if (node.text) return node.text;
  const parts = (node.content || []).map(adfToText).filter((part) => part !== '');
  return parts.join(node.type === 'doc' ? '\n' : '');
};

const asText = (value, fallback = '') => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return String(fallback);
};

const getSubmittedObjectOptionByJiraField = (questionsMap, stateAnswers, jiraField) => {
  if (!questionsMap || !stateAnswers || !jiraField) return null;

  for (const [qId, question] of Object.entries(questionsMap)) {
    if (question?.jiraField !== jiraField) continue;

    const answer = stateAnswers[qId];
    const rawSelectedChoice = answer?.choices?.[0];
    const selectedChoiceId = typeof rawSelectedChoice === 'object' ? rawSelectedChoice?.id : rawSelectedChoice;
    if (!selectedChoiceId) continue;

    const selectedChoice = question?.choices?.find((choice) => String(choice?.id) === String(selectedChoiceId));
    const answerLabel = typeof rawSelectedChoice === 'object' ? (rawSelectedChoice?.label || rawSelectedChoice?.name) : null;
    return {
      value: String(selectedChoiceId),
      label: asText(selectedChoice?.label || answerLabel, selectedChoice?.id || selectedChoiceId),
    };
  }

  return null;
};

export default function IssueForm({ readOnly = false }) {
  const { t } = useTranslation();
  const { issueKey: issueKeyParam, formId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { serviceDesk, currentUser } = useContext(AppContext);
  const serviceDeskConfig = serviceDesk?.config;

  const [data, setData] = useState(null);
  const [formBundle, setFormBundle] = useState(null); // full API payload to derive issueKey if needed
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userCtx, setUserCtx] = useState({ sectors: [], role: 'user', accountId: null });
  const [issue, setIssue] = useState(location.state?.issue || null);
  const [resolvedIssueId, setResolvedIssueId] = useState(location.state?.issue?.issueId || null);
  // location.state first (in-app navigation), URL param as fallback so a
  // direct load / refresh of the form page still resolves the issue.
  const effectiveIssueKey = issue?.key || issueKeyParam;

  // Unified answers state: { [apiKey]: { type: 'ob'|'no'|'st', value: any } }
  const [formAnswers, setFormAnswers] = useState({});
  const [optionsByField, setOptionsByField] = useState({}); // { [jiraField]: Array<option> }
  const [loadingField, setLoadingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({}); // { [qid]: 'message' }
  const [cmdbFieldsOrder, setCmdbFieldsOrder] = useState([]);
  const [cmdbFieldsSet, setCmdbFieldsSet] = useState(() => new Set());
  const [lastLoadedFormId, setLastLoadedFormId] = useState(null); // Track which form data was last loaded

  // Determine if issue is open for editing based on status
  const isIssueOpen = useMemo(() => {
    if (!serviceDeskConfig?.issueValidation?.enabled) return true;
    return isDraftIssue(issue, serviceDeskConfig);
  }, [issue, serviceDeskConfig]);

  const isTechnician = currentUser?.role === 'technician';
  const formTitleSource = (
    data?.design?.settings?.name ||
    data?.title ||
    data?.name ||
    formBundle?.form?.name ||
    ''
  );
  const formTitle = typeof formTitleSource === 'string' ? formTitleSource : String(formTitleSource || '');
  const isTechForm = formTitle.toLowerCase().includes('[tech]');
  const formStateStatus = formBundle?.state?.status ?? data?.state?.status;
  const isFormOpenForEdit = formStateStatus === 'o';
  const canTechnicianOverride = isTechnician && isTechForm && isFormOpenForEdit;
  const canEditForm = !readOnly && (isIssueOpen || canTechnicianOverride);

  const orderedQuestions = useMemo(() => {
    if (!data?.design) return [];
    const qMap = data.design.questions || {};
    const layouts = data.design.layout || [];
    const topIds = [];

    // Parse all layouts, not just the first one
    layouts.forEach(layout => {
      const content = layout?.content || [];
      content.forEach(n => {
        if (n?.type === 'extension' && n?.attrs?.extensionKey === 'question') {
          topIds.push(String(n?.attrs?.parameters?.id));
        } else if (n?.type === 'layoutSection') {
          (n.content || []).forEach(col => {
            (col?.content || []).forEach(cn => {
              if (cn?.type === 'extension' && cn?.attrs?.extensionKey === 'question') {
                topIds.push(String(cn?.attrs?.parameters?.id));
              }
            });
          });
        }
      });
    });

    return topIds
      .map(id => ({ id, ...qMap[id] }))
      .filter(Boolean);
  }, [data]);

  // Resolve issue from navigation state if provided
  useEffect(() => {
    if (location.state?.issue && !issue) {
      setIssue(location.state.issue);
      if (!resolvedIssueId && location.state.issue.issueId) setResolvedIssueId(location.state.issue.issueId);
    }
  }, [location.state, issue, resolvedIssueId]);

  // If we don't have an issueId yet, fetch the issue by issueKey (from URL/state)
  useEffect(() => {
    let cancelled = false;
    async function fetchIssueByKey(key) {
      try {
        const res = await fetchWithAuth(`/issues/${encodeURIComponent(key)}`);
        const issue = await res.json();

        setIssue(issue);
        setResolvedIssueId(issue.id);

      } catch (err) {
        if (!cancelled) setError(err?.message || t('Unknown error'));
      }
    }
    if (!resolvedIssueId && effectiveIssueKey) {
      fetchIssueByKey(effectiveIssueKey);
    }
    return () => { cancelled = true; };
  }, [resolvedIssueId, effectiveIssueKey]);

  // Once we have an issueId (from state or fetched), fetch the form content
  useEffect(() => {
    let cancelled = false;
    async function loadForm(iId) {
      setLoading(true);
      setError('');
      try {
        const res = await fetchWithAuth(`/issues/${iId}/forms/${formId}`);
        const body = await res.json();
        if (!res.ok || body?.success === false) {
          throw new Error(body?.error || `HTTP ${res.status}`);
        }
        const full = body?.data ?? body;

        if (!cancelled) {
          setData(full?.form ?? full);
          setFormBundle(full);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || t('Unknown error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    const iId = resolvedIssueId || issue?.issueId || null;
    if (iId && formId) {
      loadForm(iId);
    }
    return () => { cancelled = true; };
  }, [resolvedIssueId, issue, formId]);

  // Initialize answers from API returned state.answers and design.questions choices
  useEffect(() => {
    if (!data?.design || !data?.state?.answers) return;
    const qMap = data.design.questions || {};

    // if the form is still open (not answered once), filter out answers that match jiraField questions
    // Jira is pre-filling the connected fields with the issue status values and it mixes up things
    // with our multiple forms using the same issue field value
    let stateAnswers = data.state.answers || {};

    if (data.state.status === 'o') {
      // Form not submitted - filter out answers for questions that have jiraField
      const filteredAnswers = {};
      Object.entries(stateAnswers).forEach(([qid, ans]) => {
        const q = qMap[qid];
        if (!q || !q.jiraField) {
          // Keep answers for questions without jiraField
          filteredAnswers[qid] = ans;
        }
      });
      stateAnswers = filteredAnswers;
    }

    const initialFormAnswers = {};
    const initialOptionsByField = {};

    Object.entries(stateAnswers).forEach(([qid, ans]) => {
      const q = qMap[qid];
      if (!q) return;
      if (q.type === 'ob' && q.jiraField) {
        if (data.state.status !== 'o' && DESIGN_ANSWER_FALLBACK_FIELDS.has(String(q.jiraField))) {
          return;
        }
        const choice = Array.isArray(ans?.choices) && ans.choices.length ? ans.choices[0] : null;
        if (!choice) return;
        const selectedId = typeof choice === 'string' ? choice : choice.id;
        const selectedName = typeof choice === 'object' ? (choice.name || choice.label) : null;
        // Prefer human label from design.choices if the saved 'name' equals the id
        const rawLabel =
          ((selectedName && String(selectedName) !== String(selectedId)) ? selectedName : null)
          || (q.choices || []).find(c => c.id === selectedId)?.label
          || String(selectedId || '');
        const option = { value: String(selectedId), label: asText(rawLabel, selectedId || '') };
        initialFormAnswers[q.jiraField] = { type: 'ob', value: option };
        initialOptionsByField[q.jiraField] = [option];
      } else if (q.type === 'cs' || q.type === 'cd') {
        const first = Array.isArray(ans?.choices) && ans.choices.length ? ans.choices[0] : null;
        const selectedId = typeof first === 'string' ? first : first?.id;
        if (selectedId) initialFormAnswers[`${qid}`] = { type: q.type, value: String(selectedId) };
      } else if (q.type === 'cl' || q.type === 'cm') {
        const choices = Array.isArray(ans?.choices) ? ans.choices : [];
        const selectedIds = choices
          .map(choice => (typeof choice === 'string' ? choice : choice?.id))
          .filter(Boolean)
          .map(id => String(id));
        if (selectedIds.length) {
          initialFormAnswers[`${qid}`] = { type: q.type, value: selectedIds };
        }
      } else if (q.type === 'no') {
        initialFormAnswers[`${qid}`] = { type: 'no', value: String(ans?.text ?? '') };
      } else if (q.type === 'tl') {
        initialFormAnswers[`${qid}`] = { type: 'tl', value: String(ans?.text ?? '') };
      } else if (q.type === 'ts') {
        initialFormAnswers[`${qid}`] = { type: 'ts', value: String(ans?.text ?? '') };
      } else if (q.type === 'rt') {
        initialFormAnswers[`${qid}`] = { type: 'rt', value: ans?.adf ? adfToText(ans.adf) : String(ans?.text ?? '') };
      } else if (q.type === 'at') {
        // For attachments, use files array if available, otherwise empty array
        const files = Array.isArray(ans?.files) ? ans.files : [];
        initialFormAnswers[`${qid}`] = { type: 'at', value: files };
      } else if (typeof ans?.text !== 'undefined') {
        initialFormAnswers[`${qid}`] = { type: 'st', value: String(ans.text) };
      }
    });

    if (data.state.status !== 'o') {
      DESIGN_ANSWER_FALLBACK_FIELDS.forEach((jiraField) => {
        const option = getSubmittedObjectOptionByJiraField(qMap, stateAnswers, jiraField);
        if (!option) return;
        initialFormAnswers[jiraField] = { type: 'ob', value: option };
        initialOptionsByField[jiraField] = [option];
      });
    }

    const currentFormId = data?.id;

    // Always reload form answers when data changes from server
    // The "isNewForm" check was meant to preserve user edits during live editing,
    // but when returning to a saved form, we should always show the saved values
    setFormAnswers(initialFormAnswers);
    setOptionsByField(initialOptionsByField);
    setLastLoadedFormId(currentFormId);
  }, [data, lastLoadedFormId]);

  // Load user context (role, sectors) from /me using localStorage creds
  useEffect(() => {
    let cancelled = false;
    async function loadUser() {
      try {
        const username = localStorage.getItem('userName');
        const userId = localStorage.getItem('userId');
        if (!username || !userId) return;
        const res = await fetchWithAuth('/me', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, userId })
        });
        const body = await res.json();
        if (res.ok && body?.success && !cancelled) {
          const user = body.data?.user || {};
          setUserCtx({ sectors: user.sectors || [], role: user.role || 'user', accountId: user.accountId || userId });
          try { console.log('[IssueForm] /me userCtx', { sectors: user.sectors || [], role: user.role || 'user', accountId: user.accountId || userId }); } catch (_) {}
        }
      } catch (_) {}
    }
    // loadUser();
    return () => { cancelled = true; };
  }, []);

  const mapOptions = (payload) => {
    const values = payload?.values || payload?.data?.values || payload || [];
    const arr = Array.isArray(values) ? values : [];
    return arr.map(v => ({
      value: v.globalId || v.value || v.id || v.objectId || v.key,
      label: asText(v.label || v.name || v.displayName || v.objectLabel, v.id || v.key || v.globalId),
      key: v.objectKey || v.key
    }));
  };

  // Derive CMDB fields (chain order) from externalData and layout order
  useEffect(() => {
    const extFields = formBundle?.externalData?.fields || {};
    const cmdbSet = new Set();
    Object.values(extFields).forEach((entry) => {
      const jf = entry?.jiraField || {};
      if (jf?.custom === 'com.atlassian.jira.plugins.cmdb:cmdb-object-cftype' && jf?.id) {
        cmdbSet.add(String(jf.id));
      }
    });
    // Order by appearance in layout (orderedQuestions)
    const ordered = (orderedQuestions || [])
      .map((q) => q.jiraField)
      .filter((fid) => fid && cmdbSet.has(String(fid)));
    setCmdbFieldsSet(cmdbSet);
    setCmdbFieldsOrder(ordered);
  }, [formBundle, orderedQuestions]);

  // Memoized so useCmdbChain's prefetch effect doesn't re-fire (and burst requests)
  // on every render. Only changes when the actual user context changes.
  const cmdbUserCtx = useMemo(
    () => ({ sectors: userCtx.sectors || [], role: userCtx.role || 'user', accountId: userCtx.accountId || null }),
    [userCtx],
  );

  // CMDB chain hook
  const cmdb = useCmdbChain({
    fieldsOrder: cmdbFieldsOrder,
    getCurrentValue: (fieldId) => formAnswers?.[fieldId]?.value || null,
    setValue: (fieldId, option) => {
      setFormAnswers((prev) => ({ ...prev, [String(fieldId)]: option ? { type: 'ob', value: option } : undefined }));
    },
    clearValue: (fieldId) => {
      setFormAnswers((prev) => {
        const copy = { ...prev };
        delete copy[String(fieldId)];
        return copy;
      });
    },
    fetcher: async (fieldId, selections, uctx) => {
      const params = new URLSearchParams();
      Object.entries(selections || {}).forEach(([k, v]) => {
        const label = v?.label || '';
        const idVal = v?.value || '';
        if (label) params.append(String(k), String(label));
        if (idVal) params.append(`${String(k)}Id`, String(idVal));
      });
      if (Array.isArray(uctx?.sectors) && uctx.sectors.length) params.append('userSectors', uctx.sectors.join(','));
      if (uctx?.role) params.append('userRole', uctx.role);
      if (uctx?.accountId) params.append('userAccountId', uctx.accountId);
      const templateId = formBundle?.templateId;

      if (templateId) {
        params.append('formId', templateId);
      }

      const qs = params.toString();
      const url = `assets/${encodeURIComponent(fieldId)}${qs ? `?${qs}` : ''}`;
      try { console.log('[IssueForm] CMDB fetcher', { fieldId, selections, userCtx: uctx, qs, url }); } catch (_) {}
      const res = await fetchWithAuth(url);
      const body = await res.json();
      const arr = mapOptions(body);
      try { console.log('[IssueForm] CMDB fetcher result', { fieldId, count: arr.length }); } catch (_) {}
      return arr;
    },
    userCtx: cmdbUserCtx,
    autoSelectSingle: true,
  });

  const buildQueryFromAnswers = () => {
    const params = new URLSearchParams();
    Object.entries(formAnswers).forEach(([key, entry]) => {
      if (!entry) return;
      // Only pass object (assets) answers to help dependent lookups
      if (entry.type === 'ob' && key.startsWith('customfield_')) {
        const label = entry.value?.label || '';
        const idVal = entry.value?.value || '';
        if (label) params.append(key, label);
        if (idVal) params.append(`${key}Id`, String(idVal));
      }
    });
    if (userCtx.sectors?.length) params.append('userSectors', userCtx.sectors.join(','));
    if (userCtx.role) params.append('userRole', userCtx.role);
    if (userCtx.accountId) params.append('userAccountId', userCtx.accountId);
    const templateId = formBundle?.templateId;
    if (templateId) params.append('formId', templateId);
    return params.toString();
  };

  const fetchOptionsForField = async (jiraField) => {
    try {
      // Skip refetch if we already have options cached for this field
      if (Array.isArray(optionsByField[jiraField]) && optionsByField[jiraField].length > 0) {
        try { console.log('[IssueForm] fetchOptionsForField skip (cached)', { jiraField, count: optionsByField[jiraField].length }); } catch (_) {}
        return;
      }
      setLoadingField(jiraField);
      const qs = buildQueryFromAnswers();
      const url = `assets/${jiraField}${qs ? `?${qs}` : ''}`;
      try { console.log('[IssueForm] fetchOptionsForField', { jiraField, qs, url }); } catch (_) {}
      const res = await fetchWithAuth(url);
      const body = await res.json();
      const fetched = mapOptions(body);
      try { console.log('[IssueForm] fetchOptionsForField result', { jiraField, count: fetched.length }); } catch (_) {}
      // Ensure current selected option (if any) is present in options, then de-duplicate by value
      const current = formAnswers[jiraField]?.value;
      const merged = current ? [current, ...fetched] : fetched;
      const dedup = [];
      const seen = new Set();
      for (const o of merged) {
        const key = o?.value ? String(o.value) : '';
        if (!key || seen.has(key)) continue;
        seen.add(key);
        dedup.push({ value: String(o.value), label: asText(o.label, o.value) });
      }
      setOptionsByField(prev => ({ ...prev, [jiraField]: dedup }));
    } catch (e) {
      setOptionsByField(prev => ({ ...prev, [jiraField]: [] }));
    } finally {
      setLoadingField(null);
    }
  };

  const handleSelect = (jiraField, option) => {
    // If cleared, remove the answer entry to avoid stale value
    if (!option) {
      setFormAnswers(prev => {
        const copy = { ...prev };
        delete copy[jiraField];
        return copy;
      });
      return;
    }
    setFormAnswers(prev => ({ ...prev, [jiraField]: { type: 'ob', value: option } }));
    // Clear subsequent answers and options (simple dependency handling)
    const idx = orderedQuestions.findIndex(q => q.jiraField === jiraField);
    if (idx >= 0) {
      const toClear = orderedQuestions.slice(idx + 1).map(q => q.jiraField);
      setFormAnswers(prev => {
        const copy = { ...prev };
        toClear.forEach(f => { if (f) delete copy[f]; });
        return copy;
      });
      setOptionsByField(prev => {
        const copy = { ...prev };
        toClear.forEach(f => { if (f) delete copy[f]; });
        return copy;
      });
    }
  };

  const handleRemoveFile = async (qid, fileToRemove, fileIndex) => {
    try {
      const currentFiles = formAnswers[qid]?.value || [];
      const newFiles = currentFiles.filter((_, i) => i !== fileIndex);

      setFormAnswers(prev => {
        return {
          ...prev,
          [qid]: { type: 'at', value: newFiles }
        };
      });

      if (fileToRemove.jiraAttachmentId && effectiveIssueKey) {
        await fetchWithAuth(
          `/issues/${encodeURIComponent(effectiveIssueKey)}/attachments/${fileToRemove.jiraAttachmentId}`,
          { method: 'DELETE' }
        );
      } else {
        console.warn('[IssueForm] File has no jiraAttachmentId, skipping Jira deletion');
      }
    } catch (err) {
      console.error('[IssueForm] Error removing file:', err);
    }
  };

  const handleImageUpload = async (qid, files) => {
    if (!files || files.length === 0) return;

    const currentFiles = formAnswers[qid]?.value || [];
    const remainingSlots = MAX_FILES - (Array.isArray(currentFiles) ? currentFiles.length : 0);
    let filesToUpload = Array.from(files);

    if (filesToUpload.length > remainingSlots) {
      filesToUpload = filesToUpload.slice(0, remainingSlots);
    }

    if (filesToUpload.length === 0) return;

    const tempFiles = filesToUpload.map(file => ({
      id: Math.random().toString(36).substring(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file),
      isUploading: true,
      file
    }));

    setFormAnswers(prev => {
      const existing = prev[qid]?.value || [];
      return {
        ...prev,
        [qid]: {
          type: 'attachment',
          value: [...existing, ...tempFiles]
        }
      };
    });

    for (const tempFile of tempFiles) {
      try {
        const formData = new FormData();
        formData.append('files', tempFile.file);

        const effectiveServiceDeskId = issue.serviceDeskId || serviceDesk?.id;
        const uploadUrl = effectiveServiceDeskId
          ? `/attachments/upload?serviceDeskId=${effectiveServiceDeskId}`
          : `/attachments/upload`;

        const res = await fetchWithAuth(uploadUrl, {
          method: 'POST',
          body: formData
        });

        if (!res.ok) {
          throw new Error('Upload failed');
        }

        const temporaryAttachments = await res.json();

        if (Array.isArray(temporaryAttachments) && temporaryAttachments.length) {
          const uploadedAttachment = {
            id: temporaryAttachments[0].temporaryAttachmentId,
            name: temporaryAttachments[0].fileName || tempFile.name,
            creationDate: Date.now(),
            size: tempFile.size,
            type: tempFile.type,
            url: tempFile.url,
            isUploading: false
          };

          setFormAnswers(prev => {
            const currentFiles = prev[qid]?.value || [];
            const filesArray = Array.isArray(currentFiles) ? currentFiles : [];
            const updatedFiles = filesArray.map(f =>
              f.id === tempFile.id ? uploadedAttachment : f
            );
            return {
              ...prev,
              [qid]: { type: 'at', value: updatedFiles }
            };
          });

          setFieldErrors(prev => {
            const copy = { ...prev };
            delete copy[qid];
            return copy;
          });
        }
      } catch (err) {
        console.error('Error uploading file:', err);
        setFormAnswers(prev => {
          const currentFiles = prev[qid]?.value || [];
          const filesArray = Array.isArray(currentFiles) ? currentFiles : [];
          const updatedFiles = filesArray.map(f =>
            f.id === tempFile.id ? { ...f, isUploading: false, uploadFailed: true } : f
          );
          return {
            ...prev,
            [qid]: { type: 'at', value: updatedFiles }
          };
        });
      }
    }
  };

  const toggleChoiceMultiple = (questionKey, choiceId, isSelected, qid) => {
    const currentIds = Array.isArray(formAnswers[questionKey]?.value) ? formAnswers[questionKey].value : [];
    const updatedIds = isSelected
      ? currentIds.filter(id => String(id) !== choiceId)
      : [...currentIds, choiceId];

    setFormAnswers(prev => {
      const updated = { ...prev };
      if (updatedIds.length > 0) {
        updated[questionKey] = { type: 'cm', value: updatedIds };
      } else {
        delete updated[questionKey];
      }
      return updated;
    });

    if (updatedIds.length > 0) {
      setFieldErrors(prev => {
        const copy = { ...prev };
        delete copy[qid];
        return copy;
      });
    }
  };

  const isSectionVisible = (sectionId) => isProformaSectionVisible(data?.design, formAnswers, sectionId);

  const renderQuestion = (qid) => {
    const q = data?.design?.questions?.[qid];
    if (!q) return null;
    const isObject = q.type === 'ob';
    const isNumber = q.type === 'no';
    const isChoiceSingle = q.type === 'cs';
    const isChoiceMultiple = q.type === 'cm';
    const isChoiceDropdown = q.type === 'cd';
    const isChoiceMultiList = q.type === 'cl';
    const isTextShort = q.type === 'ts';
    const isTextLong = q.type === 'tl';
    const isRichText = q.type === 'rt';
    const isAttachment = q.type === 'at';
    const isDate = q.type === 'da';
    const jiraField = q.jiraField;
    const apiKey = q.type === 'ob' && jiraField ? jiraField : String(qid);
    const valueObj = formAnswers[apiKey];
    let value;
    if (q.type === 'ob') {
      value = valueObj?.value || null;
    } else if (q.type === 'cl' || q.type === 'cm') {
      value = Array.isArray(valueObj?.value) ? valueObj.value : [];
    } else {
      value = valueObj?.value ?? '';
    }
    const isCmdbField = jiraField ? cmdbFieldsSet.has(String(jiraField)) : false;
    const options = jiraField
      ? (isCmdbField ? (cmdb.optionsByField[jiraField] || []) : (optionsByField[jiraField] || []))
      : [];
    const normalizedSelectOptions = options.map((opt) => ({
      ...opt,
      value: String(opt?.value || ''),
      label: asText(opt?.label, opt?.value || ''),
    }));
    const hasError = Boolean(fieldErrors[qid]);
    const inputClass = `mt-1 block w-full rounded-md border p-2 text-sm ${hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`;

    // Get attachment files array from the value
    const attachmentFiles = isAttachment && Array.isArray(value) ? value : [];
    const isUploading = attachmentFiles.some(f => f.isUploading);

    return (
      <div key={qid}>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {q.label} {q.validation?.rq ? <span className="text-red-500">*</span> : null}
        </label>
        {isObject ? (
          <Select
            options={normalizedSelectOptions}
            value={value ? { value: String(value.value), label: asText(value.label, value.value) } : null}
            isClearable
            isSearchable
            isDisabled={!canEditForm || (isCmdbField ? !cmdb.isFieldEnabled(jiraField) : false)}
            placeholder={t('Select an option…')}
            classNamePrefix="select"
            onMenuOpen={() => {
              if (!jiraField) return;
              if (isCmdbField && !cmdb.isFieldEnabled(jiraField)) return;
            try { console.log('[IssueForm] onMenuOpen', { jiraField, isCmdbField }); } catch (_) {}
            if (isCmdbField) cmdb.loadOptionsFor(jiraField);
              else fetchOptionsForField(jiraField);
            }}
            onChange={(opt) => {
              if (jiraField) {
                if (isCmdbField) cmdb.handleSelect(jiraField, opt);
                else handleSelect(jiraField, opt);
              }
              if (opt && opt.value) {
                setFieldErrors(prev => {
                  const copy = { ...prev };
                  delete copy[qid];
                  return copy;
                });
              }
            }}
            isLoading={(loadingField === jiraField) || (cmdb.loadingField === jiraField)}
          />
        ) : isChoiceSingle ? (
          <div className="space-y-2">
            {(q.choices || []).map((c) => {
              const optionId = String(c.id);
              const isChecked = String(value || '') === optionId;

              // Custom radio-like behavior using divs/buttons to avoid native radio quirks
              // and ensure "click to toggle" works reliably without name collisions.
              return (
                <div
                  key={optionId}
                  className={`flex items-center gap-2 rounded border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1
                    ${isChecked ? 'border-black bg-gray-100 text-black' : 'border-gray-200 bg-white text-gray-700'}
                    ${!canEditForm ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer' + (!isChecked ? ' hover:border-gray-300 hover:bg-gray-50' : '')}`}
                  onClick={(e) => {
                    if (!canEditForm) return;
                    e.preventDefault(); // Prevent label behavior if any

                    if (isChecked) {
                      // Deselect if already checked
                      setFormAnswers(prev => {
                        const copy = { ...prev };
                        delete copy[String(qid)];
                        return copy;
                      });
                    } else {
                      // Select new value
                      setFormAnswers(prev => ({ ...prev, [String(qid)]: { type: 'cs', value: optionId } }));
                      setFieldErrors(prev => {
                        const copy = { ...prev };
                        delete copy[qid];
                        return copy;
                      });
                    }
                  }}
                  role="radio"
                  aria-checked={isChecked}
                  tabIndex={canEditForm ? 0 : -1}
                  onKeyDown={(e) => {
                    if (!canEditForm) return;
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      if (isChecked) {
                        setFormAnswers(prev => {
                          const copy = { ...prev };
                          delete copy[String(qid)];
                          return copy;
                        });
                      } else {
                        setFormAnswers(prev => ({ ...prev, [String(qid)]: { type: 'cs', value: optionId } }));
                        setFieldErrors(prev => {
                          const copy = { ...prev };
                          delete copy[qid];
                          return copy;
                        });
                      }
                    }
                  }}
                >
                  <div className={`relative flex h-4 w-4 items-center justify-center rounded-full border ${isChecked ? 'border-black' : 'border-gray-400'}`}>
                    {isChecked && (
                      <div className="h-2 w-2 rounded-full bg-black" />
                    )}
                  </div>
                  <span className="select-none">{c.label}</span>
                </div>
              );
            })}
          </div>
        ) : isChoiceMultiple ? (
          <div className="space-y-2">
            {(q.choices || []).map((choice) => {
              const choiceId = String(choice.id);
              const isSelected = Array.isArray(value) && value.includes(choiceId);
              const questionKey = String(qid);

              return (
                <div
                  key={choiceId}
                  className={`flex items-center gap-2 rounded border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1
                    ${isSelected ? 'border-black bg-gray-100 text-black' : 'border-gray-200 bg-white text-gray-700'}
                    ${!canEditForm ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer' + (!isSelected ? ' hover:border-gray-300 hover:bg-gray-50' : '')}`}
                  onClick={(e) => {
                    if (!canEditForm) return;
                    e.preventDefault();
                    toggleChoiceMultiple(questionKey, choiceId, isSelected, qid);
                  }}
                  role="checkbox"
                  aria-checked={isSelected}
                  tabIndex={canEditForm ? 0 : -1}
                  onKeyDown={(e) => {
                    if (!canEditForm) return;
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      toggleChoiceMultiple(questionKey, choiceId, isSelected, qid);
                    }
                  }}
                >
                  <div className={`relative flex h-4 w-4 items-center justify-center rounded border ${isSelected ? 'border-black bg-black' : 'border-gray-400 bg-white'}`}>
                    {isSelected && (
                      <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="select-none">{choice.label}</span>
                </div>
              );
            })}
          </div>
        ) : isChoiceDropdown ? (
          <Select
            options={(q.choices || []).map(c => ({ value: String(c.id), label: c.label }))}
            value={value ? { value: String(value), label: (q.choices || []).find(c => String(c.id) === String(value))?.label || String(value) } : null}
            isClearable
            isSearchable
            isDisabled={!canEditForm}
            placeholder={t('Select an option…')}
            classNamePrefix="select"
            onChange={(opt) => {
              const next = opt?.value || '';
              setFormAnswers(prev => ({ ...prev, [String(qid)]: { type: 'cd', value: next } }));
              if (next) {
                setFieldErrors(prev => {
                  const copy = { ...prev };
                  delete copy[qid];
                  return copy;
                });
              }
            }}
          />
        ) : isChoiceMultiList ? (
          <Select
            isMulti
            options={(q.choices || []).map(c => ({ value: String(c.id), label: c.label }))}
            value={
              Array.isArray(value)
                ? value.map(id => ({
                    value: String(id),
                    label:
                      (q.choices || []).find(c => String(c.id) === String(id))?.label ||
                      String(id),
                  }))
                : []
            }
            isClearable
            isSearchable
            isDisabled={!canEditForm}
            placeholder={t('Select one or more options…')}
            classNamePrefix="select"
            onChange={(opts) => {
              const next = Array.isArray(opts) ? opts.map(o => o.value) : [];
              setFormAnswers(prev => ({
                ...prev,
                [String(qid)]: { type: 'cl', value: next },
              }));
              if (next.length > 0) {
                setFieldErrors(prev => {
                  const copy = { ...prev };
                  delete copy[qid];
                  return copy;
                });
              }
            }}
          />
        ) : isNumber ? (
          <input
            type="number"
            className={inputClass}
            value={value}
            disabled={!canEditForm}
            onChange={(e) => {
              const next = e.target.value;
              setFormAnswers(prev => ({ ...prev, [apiKey]: { type: 'no', value: next } }));
              if (String(next).trim() !== '') {
                setFieldErrors(prev => {
                  const copy = { ...prev };
                  delete copy[qid];
                  return copy;
                });
              }
            }}
          />
        ) : isTextLong ? (
          <textarea
            rows={4}
            className={inputClass}
            value={value}
            disabled={!canEditForm}
            onChange={(e) => {
              const next = e.target.value;
              setFormAnswers(prev => ({ ...prev, [String(qid)]: { type: 'tl', value: next } }));
              if (String(next).trim() !== '') {
                setFieldErrors(prev => {
                  const copy = { ...prev };
                  delete copy[qid];
                  return copy;
                });
              }
            }}
          />
        ) : isTextShort ? (
          <input
            type="text"
            className={inputClass}
            value={value}
            disabled={!canEditForm}
            onChange={(e) => {
              const next = e.target.value;
              setFormAnswers(prev => ({ ...prev, [String(qid)]: { type: 'ts', value: next } }));
              if (String(next).trim() !== '') {
                setFieldErrors(prev => {
                  const copy = { ...prev };
                  delete copy[qid];
                  return copy;
                });
              }
            }}
          />
        ) : isRichText ? (
          <textarea
            rows={6}
            className={inputClass}
            value={value}
            disabled={!canEditForm}
            onChange={(e) => {
              const next = e.target.value;
              setFormAnswers(prev => ({ ...prev, [String(qid)]: { type: 'rt', value: next } }));
              if (String(next).trim() !== '') {
                setFieldErrors(prev => {
                  const copy = { ...prev };
                  delete copy[qid];
                  return copy;
                });
              }
            }}
            placeholder={t('Enter your comment…')}
          />
        ) : isAttachment ? (
          <div>
            {(() => {
              const currentFilesCount = attachmentFiles.length;
              const isLimitReached = currentFilesCount >= MAX_FILES;

              return (
                <>
                  <div className="mt-1">
                    <input
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      multiple
                      className="hidden"
                      id={`file-upload-${qid}`}
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) {
                          handleImageUpload(qid, Array.from(files));
                          e.target.value = '';
                        }
                      }}
                      disabled={isUploading || !canEditForm || isLimitReached}
                    />
                    <label
                      htmlFor={`file-upload-${qid}`}
                      className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer ${(isUploading || !canEditForm || isLimitReached) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                {isUploading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('Uploading…')}
                  </>
                ) : (
                  <>
                    <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    {t('Add an attachment')}
                  </>
                )}
                    </label>
                  </div>
                </>
              );
            })()}

            {attachmentFiles.length > 0 && (
              <div className="mt-3">
                <AttachmentsGallery
                  attachments={attachmentFiles.map(file => ({
                    filename: file.name,
                    url: file.url,
                    thumbnail: file.thumbnail,
                    id: file.id,
                    isUploading: file.isUploading,
                    uploadFailed: file.uploadFailed
                  }))}
                  editable={canEditForm}
                  onDelete={(_, index) => handleRemoveFile(qid, attachmentFiles[index], index)}
                />
              </div>
            )}
            <div className="mt-3">
              <UploadProgressIndicator uploadCount={attachmentFiles.filter(f => f.isUploading).length || 0} />
            </div>
          </div>
        ) : isDate ? (
          <input
            type="date"
            className={inputClass}
            value={value}
            disabled={!canEditForm}
            onChange={(e) => {
              const next = e.target.value;
              setFormAnswers(prev => ({ ...prev, [apiKey]: { type: 'da', value: next } }));
              if (String(next).trim() !== '') {
                setFieldErrors(prev => {
                  const copy = { ...prev };
                  delete copy[qid];
                  return copy;
                });
              }
            }}
          />
        ) : (
          <input type="text" className={inputClass} readOnly value="" />
        )}

        {q.description ? (
          <p className="mt-1 text-sm text-gray-500 flex items-center gap-1">
            <InfoCircledIcon className="w-3 h-3" />
            {q.description}
          </p>
        ) : null}

        {hasError ? (
          <p className="mt-1 text-xs text-red-600">{t('This field is required')}</p>
        ) : null}
      </div>
    );
  };

  const isQuestionVisible = (questionId) => isProformaQuestionVisible(data?.design, formAnswers, questionId);

  const onSaveAndSubmit = async () => {
    try {
      setActionError('');
      setSaving(true);

      // Front required validation (same as onSave/onSubmit)
      const errors = {};
      (orderedQuestions || []).forEach((q) => {
        if (!q?.validation?.rq) return;
        const qid = String(q.id);

        // Skip validation for hidden fields
        if (!isQuestionVisible(qid)) return;

        if (q.type === 'ob' && q.jiraField) {
          const sel = formAnswers[q.jiraField]?.value?.value;
          if (!sel) errors[qid] = 'required';
        } else {
          const v = formAnswers[qid]?.value;
          const isEmpty =
            v === undefined ||
            v === null ||
            (Array.isArray(v) ? v.length === 0 : String(v).trim() === '');
          if (isEmpty) errors[qid] = 'required';
        }
      });

      setFieldErrors(errors);

      if (Object.keys(errors).length > 0)
        throw new Error(t('Please fill in the required fields'));

      // Build payload like onSave
      const answersById = {};
      orderedQuestions.forEach((q) => {
        const qid = String(q.id);
        if (q.type === 'ob' && q.jiraField) {
          const sel = formAnswers[q.jiraField]?.value?.value;
          if (sel) answersById[qid] = { choices: [String(sel)] };
        } else if (q.type === 'cs' || q.type === 'cd') {
          const sel = formAnswers[qid]?.value;
          if (sel) answersById[qid] = { choices: [String(sel)] };
        } else if (q.type === 'cl' || q.type === 'cm') {
          const sel = formAnswers[qid]?.value;
          if (Array.isArray(sel) && sel.length) {
            answersById[qid] = { choices: sel.map(id => String(id)) };
          }
        } else if (q.type === 'at') {
          // For attachments, send files array without URL, jiraId, jiraFilename
          const files = formAnswers[qid]?.value;
          // Always send the files array, even if empty (to clear deleted files in Jira)
          if (Array.isArray(files)) {
            answersById[qid] = {
              files: files.map(file => ({
                id: file.id,
                name: file.name,
                creationDate: file.creationDate,
                size: file.size,
                type: file.type
              }))
            };
          }
        } else if (q.type === 'rt') {
        const sv = formAnswers[qid]?.value;
        if (typeof sv !== 'undefined') answersById[qid] = { adf: textToAdf(String(sv ?? '')) };
      } else if (q.type === 'st' || q.type === 'ts' || q.type === 'no' || q.type === 'tl' || q.type === 'da') {
          const sv = formAnswers[qid]?.value;
          if (typeof sv !== 'undefined') answersById[qid] = { text: String(sv ?? '') };
        }
      });

      const payload = { answers: answersById };

      // Save
      const saveRes = await fetchWithAuth(`/issues/${encodeURIComponent(effectiveIssueKey)}/forms/${encodeURIComponent(formId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const saveBody = await saveRes.json();

      if (!saveRes.ok || saveBody?.success === false)
        throw new Error(saveBody?.error || `HTTP ${saveRes.status}`);

      // Submit
      const submitRes = await fetchWithAuth(`/issues/${encodeURIComponent(effectiveIssueKey)}/forms/${encodeURIComponent(formId)}/submit`, {
        method: 'PUT'
      });

      const submitBody = await submitRes.json();

      if (!submitRes.ok || submitBody?.success === false)
        throw new Error(submitBody?.error || `HTTP ${submitRes.status}`);

      navigate(`/issues/${encodeURIComponent(effectiveIssueKey)}`);

    } catch (e) {
      setActionError(e?.message || t('Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const cleanTitle = (data?.design?.settings?.name || '').replace(/\[tech\]/gi, '').trim();

  const isAnyUploading = Object.values(formAnswers).some(entry =>
    Array.isArray(entry?.value) && entry.value.some(f => f.isUploading)
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="container mx-auto py-6">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 inline-flex items-center text-black transition-colors hover:text-primary/80"
          >
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('Back')}
          </button>

          <h1 className="mb-4 text-2xl font-bold text-black">
            {isTechForm && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                {t('technician')}
              </span>
            )}
            &nbsp;{cleanTitle}
          </h1>

          {(loading || (!data && !error)) ? (
            <div className="flex justify-center py-12">
              <div className="h-14 w-14 animate-spin rounded-full border-b-2 border-black"></div>
            </div>
          ) : error ? (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
          ) : (
            <div className="rounded border border-gray-200 bg-white p-4">
              {/* Render dynamic Proforma form fields */}
              <div className="space-y-6">
                {/* Render all layout sections (multiple pages/sections) */}
                {(data?.design?.layout || []).map((section, sectionIdx) => {
                  // Section IDs start at 1, but layout[0] typically has no section
                  // layout[1] = section "1", layout[2] = section "2", etc.
                  const sectionId = sectionIdx > 0 ? String(sectionIdx) : null;

                  // Check if this section should be visible
                  if (sectionId && !isSectionVisible(sectionId)) return null;

                  return (
                    <div key={`section-${sectionIdx}`} className="space-y-6">
                      {(section?.content || []).map((node, idx) => {
                        if (node?.type === 'extension' && node?.attrs?.extensionKey === 'question') {
                          return <div key={`q-${idx}`}>{renderQuestion(String(node?.attrs?.parameters?.id))}</div>;
                        }
                        if (node?.type === 'layoutSection') {
                          return (
                            <div key={`ls-${idx}`} className="grid grid-cols-1 gap-4 md:grid-cols-3">
                              {(node.content || []).map((col, cIdx) => (
                                <div key={`col-${idx}-${cIdx}`}>
                                  {(col?.content || []).map((cn, qIdx) => (
                                    <div key={`colq-${idx}-${cIdx}-${qIdx}`}>{renderQuestion(String(cn?.attrs?.parameters?.id))}</div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  );
                })}
              </div>

              {canEditForm && (
                <div className="mt-6 flex items-center justify-end gap-2">
                  {actionError ? (
                    <div className="mr-auto rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">{actionError}</div>
                  ) : null}
                  <button
                    type="button"
                    className={`inline-flex items-center rounded border border-gray-300 bg-gray-100 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-200 ${saving || isAnyUploading ? 'opacity-60 pointer-events-none' : ''}`}
                    onClick={onSaveAndSubmit}
                    disabled={saving || isAnyUploading}
                  >
                    {isAnyUploading ? t('Uploading…') : saving ? t('Saving…') : t('Save')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


